import { getEventPropsBuilder, toSnakeCase } from '../../utils';
import type { AmplitudeEventKeys } from '../../amplitude-properties.types';
import type { TrackingEvent } from '@/tracking/tracking-manager';
import type { InteractionType } from '@/tracking/amplitude/events/general/exclusives';

export type JackpotsCTAClickTrackingData = {
    linkText: string;
};

type TrackingData = {
    module?: string | null;
    interactionType?: InteractionType | null;
} & Partial<JackpotsCTAClickTrackingData>;

type EventConfig = {
    event:
        | 'FDC Jackpots Intro Modal Viewed'
        | 'FDC Jackpots Intro Modal Closed'
        | 'FDC Jackpots Intro Modal CTA Clicked';
    module: string;
    getInteractionType?: (data: TrackingData) => string;
};

const EVENT_CONFIGS: Record<string, EventConfig> = {
    jackpotsIntroViewed: {
        event: 'FDC Jackpots Intro Modal Viewed',
        module: 'fdc_jackpots_intro_modal'
    },
    jackpotsIntroClosed: {
        event: 'FDC Jackpots Intro Modal Closed',
        module: 'fdc_jackpots_intro_modal',
        getInteractionType: (data) => toSnakeCase(data.interactionType || '')
    },
    jackpotsIntroCTAClicked: {
        event: 'FDC Jackpots Intro Modal CTA Clicked',
        module: 'fdc_jackpots_intro_modal'
    }
};

const buildEventProps = getEventPropsBuilder(
    (config: EventConfig, data: TrackingData | null): Partial<AmplitudeEventKeys> => {
        if (!data) {
            return {
                event: config.event
            };
        }

        const eventProps: Partial<AmplitudeEventKeys> = {
            event: config.event,
            Module: config.module
        };

        const interactionType = config.getInteractionType ? config.getInteractionType(data) : null;
        if (interactionType) {
            eventProps['Interaction Type'] = interactionType;
        }

        if (data.linkText !== undefined && data.linkText !== null) {
            eventProps['Link Text'] = toSnakeCase(data.linkText);
        }

        return eventProps;
    }
);

export const extractTrackingData = (element: HTMLElement | null): TrackingData | null => {
    if (!element) {
        return null;
    }

    const interactionType = element.closest('[data-t-interactiontype]')?.getAttribute('data-t-interactiontype');

    return {
        interactionType: (interactionType as InteractionType) || null
    };
};

export const getDybPageName = (pageIndex: number): string =>
    pageIndex === 0 ? 'interstitial' : `swj_tour_${pageIndex}`;

type DybCTAClickProps = {
    linkText: string;
    pageName: string;
    linkUrl?: string;
};

export const getDybModalCTAClickedProps = ({
    linkText,
    pageName,
    linkUrl
}: DybCTAClickProps): Partial<AmplitudeEventKeys> => {
    const props: Partial<AmplitudeEventKeys> = {
        event: 'FDC DYB Intro Modal CTA Clicked',
        Module: 'fdc_dyb_intro_modal',
        'Page Name': pageName,
        'Link Text': linkText
    };
    if (linkUrl) {
        props['Link URL'] = linkUrl;
    }
    return props;
};

export const trackJackpotsEvent = (
    eventType: 'jackpotsIntroViewed' | 'jackpotsIntroClosed' | 'jackpotsIntroCTAClicked',
    source?: TrackingData | { current: HTMLElement | null } | null,
    additionalData?: Partial<TrackingData>
): { event: TrackingEvent; trackingData: TrackingData | null } => {
    const config = EVENT_CONFIGS[eventType];
    if (!config) {
        console.warn(`[Tracking] Unknown jackpots event type: ${eventType}`);
    }

    let trackingData: TrackingData | null = null;

    if (source) {
        if ('current' in source) {
            trackingData = extractTrackingData(source.current);
        } else {
            trackingData = source;
        }
    }

    const data = { ...trackingData, ...additionalData };
    const eventProps = buildEventProps(config, data);

    return {
        event: {
            destination: 'amplitude',
            eventProps
        },
        trackingData
    };
};
